import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Vue from 'vue'
import jwtDefaultConfig from './jwtDefaultConfig'
import store from '../../../store/index'
import router from '@/router'
import { isSafePostLoginRedirect } from '@/auth/postLoginRedirect'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  store = null

  // For Refreshing Token
  subscribers = []

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }
    this.store = store

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()
        const refreshToken = this.getRefreshToken()
        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        } else if (refreshToken) {
          config.headers.Refresh = `${this.jwtConfig.tokenType} ${refreshToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        // const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config
        if (response && response.status === 401 && originalRequest.url === this.jwtConfig.refreshEndpoint) {
          this.removeToken()
          this.removeRefreshToken()
          this.removeUserData()
          const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`
          const query = isSafePostLoginRedirect(redirect) ? { redirect } : {}
          router.push({ name: 'auth-login', query }).catch(() => {})
          this.isAlreadyFetchingAccessToken = false
          // if (this.jwtConfig.logOutCallback) this.jwtConfig.logOutCallback()
          return
        }
        // if (status === 401) {
        if (response && response.status === 401) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.removeToken()
            this.refreshToken().then(r => {
              this.isAlreadyFetchingAccessToken = false
              // Обновление могло не состояться: когда сам /authentication/refresh отвечает 401, ветка выше уже увела
              // на логин и вернула undefined. Без этой проверки здесь падало
              // `TypeError: Cannot read properties of undefined (reading 'data')` — на каждой странице админки
              // с истёкшей сессией, включая экран входа.
              const accessToken = r && r.data && r.data.accessToken
              if (!accessToken) return
              // Update accessToken in localStorage
              this.setToken(accessToken)
              this.onAccessTokenFetched(accessToken)
            })
          }
          return new Promise(resolve => {
            this.addSubscriber(accessToken => {
              // Make sure to assign accessToken according to your response.
              // Check: https://pixinvent.ticksy.com/ticket/2413870
              // Change Authorization header
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
        }

        // Some 400 validation errors are handled locally in UI with better messages.
        // Skip global toast to avoid duplicates for known refund-rules duplicate constraint.
        if (response && response.status === 400) {
          const url = String(originalRequest?.url || '')
          const msg = String(error?.response?.data?.message || '').toLowerCase()
          const isRefundRulesUrl = url.includes('refund-rules')
          const isDuplicateDaysBeforeStart = msg.includes('days_before_start')
            || msg.includes('days before start')
            || (msg.includes('refund') && msg.includes('rule') && msg.includes('already') && msg.includes('exist'))

          if (isRefundRulesUrl && isDuplicateDaysBeforeStart) {
            error.notified = true
            return Promise.reject(error)
          }
        }

        // the caller shows the failure itself (an uploader, in the file's own row)
        if (originalRequest && originalRequest.silentError) {
          return Promise.reject(error)
        }

        Vue.$toast({
          component: ToastificationContent,
          props: {
            title: 'Уведомление',
            icon: 'BellIcon',
            text: error?.response?.data?.message || error.message || 'Unknown error from axios interceptor',
            variant: 'danger',
          },
        })
        error.notified = true
        return Promise.reject(error)
      },
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  removeUserData() {
    localStorage.removeItem('userData')
  }

  removeToken() {
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
  }

  removeRefreshToken() {
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  logout() {
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
    localStorage.removeItem('userData')
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.get(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }

  getUserConnectionToken(userId) {
    return this.axiosIns.get(`${this.jwtConfig.getUserConnectionTokenEndpoint}/${userId}`)
  }

  me() {
    return this.axiosIns.get(this.jwtConfig.meEndpoint)
  }
}
